import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { rgba } from "polished";
import { FormattedMessage } from "react-intl";

import { brandLogo, kikudooLogo, BRAND } from "./brand";

const Wrapper = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 1rem 0.5rem;

  @media (${props => props.theme.tabletScreen}) {
    padding: 1.5rem 1rem 1rem;
  }
`;

const Lockup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;

  @media (${props => props.theme.tabletScreen}) {
    gap: 1.25rem;
  }
`;

/* The partner asset decodes at 244x76, so heights above ~4.75rem would upscale
 * it and soften the mark. max-width is the safety net on narrow phones: the box
 * keeps its height and object-fit letterboxes the logo rather than overflowing. */
const BrandLogo = styled.img`
  display: block;
  height: 3.25rem;
  width: auto;
  max-width: 45vw;
  object-fit: contain;

  @media (${props => props.theme.tabletScreen}) {
    height: 4.25rem;
  }
`;

/* Wider than it is tall (~2.9:1), so it is sized shorter than the partner logo
 * to keep the two sides of the lockup visually balanced. */
const KikudooLogo = styled(BrandLogo)`
  height: 2.25rem;
  max-width: 40vw;

  @media (${props => props.theme.tabletScreen}) {
    height: 3rem;
  }
`;

/* The cooperation mark: "<brand> × kikudoo" */
const Cross = styled.span`
  font-family: ${props => props.theme.fontFamilyBrand};
  font-size: 1.25rem;
  line-height: 1;
  color: ${props => rgba(props.secondaryColor, 0.8)};
  user-select: none;

  @media (${props => props.theme.tabletScreen}) {
    font-size: 1.5rem;
  }
`;

const Tagline = styled.p`
  max-width: 32rem;
  margin: 0.75rem 0 0;
  text-align: center;
  font-family: ${props => props.theme.fontFamilyBrand};
  font-size: ${props => props.theme.fontSizes.small};
  letter-spacing: 0.04em;
  color: ${props => props.primaryColor};

  @media (${props => props.theme.tabletScreen}) {
    font-size: ${props => props.theme.fontSizes.default};
  }
`;

const BrandHeader = ({ primaryColor, secondaryColor }) => (
  <Wrapper>
    <Lockup>
      <BrandLogo src={brandLogo} alt="pampers club" />
      <Cross secondaryColor={secondaryColor} aria-hidden="true">
        &times;
      </Cross>
      <KikudooLogo src={kikudooLogo} alt="kikudoo" />
    </Lockup>
    <Tagline primaryColor={primaryColor}>
      <FormattedMessage id="pages.PampersSearch.tagline" />
    </Tagline>
  </Wrapper>
);

BrandHeader.propTypes = {
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
};

BrandHeader.defaultProps = {
  primaryColor: BRAND.primary,
  secondaryColor: BRAND.secondary,
};

export default BrandHeader;
